import React, { useState, useContext, useRef } from 'react';
import classnames from 'classnames';
import Image from 'next/legacy/image';

import arrowLeft from 'public/images/arrow-left.svg';
import FlagLabel from 'components/FlagLabel';

import { BuildingPageContext } from '../../pages/[urlParam]/[urlBuildParam]';
import { BuildingPageContextInterface } from '../../apolloClient/types/Building';

import MarkdownParser from '../MarkdownParser';
import { scrollToElement } from 'src/utils/utils';
import { useIsOverflowing } from 'hooks/useIsOverflowing';

const BuildingDescription: React.FC = () => {
  const { building } = useContext(
    BuildingPageContext
  ) as BuildingPageContextInterface;
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const { boxRef, contentRef, isOverflowing, clipHeight } = useIsOverflowing(
    !descriptionOpen
  );
  // Expanded text never overflows, so keep the button while open
  const showToggle = descriptionOpen || isOverflowing;
  const { name, description } = building;

  return (
    <div
      ref={descriptionRef}
      className="flex flex-col pt-[24px] mx-0 md:pt-[33px] sm:w-1/2 lg:pr-[13px]"
    >
      <div className="relative flex flex-col justify-center mb-[37px] lg:mb-[40px] w-max">
        <FlagLabel name="About" />
      </div>
      <h2 className="mb-[10px] text-18-23-0.3 md:text-21-28-0.3 font-sohneKraftig">
        {name}
      </h2>
      {description && (
        <>
          <div
            ref={boxRef}
            className={classnames(
              'relative md:pr-1 text-14-21-0.3 md:text-16-26--0.3 lg:text-16-26-0.3',
              {
                'max-h-[11rem] md:max-h-[28rem] lg:max-h-[444px]':
                  !descriptionOpen,
                'overflow-hidden': !descriptionOpen,
              }
            )}
            // Ends the collapsed box on a whole line instead of mid-line
            style={
              !descriptionOpen && clipHeight
                ? { height: clipHeight }
                : undefined
            }
          >
            <div ref={contentRef}>
              <MarkdownParser
                markdown={description}
                classNames="text-14-21-0.3 lg:text-16-26-0.3"
              />
            </div>
            {showToggle && (
              <div
                className={classnames('absolute bottom-0 h-10 w-full', {
                  hidden: descriptionOpen,
                })}
                style={{
                  background: 'linear-gradient(rgba(255,255,255,0),#fff)',
                }}
              />
            )}
          </div>
          {showToggle && (
            <button
              onClick={() => setDescriptionOpen(!descriptionOpen)}
              className="min-w-[57px] flex items-center mt-5 text-16-26-0.3 uppercase border-b-[3px] cursor-pointer border-gold-dark md:mt-8 md:mb-10 lg:my-6 w-max"
            >
              <div className="w-[8px] h-[14px] mr-[7px]">
                <Image
                  className={classnames({
                    'transform -rotate-90': descriptionOpen,
                  })}
                  src={arrowLeft}
                  alt={`${descriptionOpen ? 'Hide' : 'Read more'}`}
                  width={8}
                  height={14}
                />
              </div>
              <span
                onClick={() =>
                  scrollToElement(descriptionRef.current, descriptionOpen)
                }
                className="pt-[6px] whitespace-nowrap"
              >
                {`${descriptionOpen ? 'Hide' : 'Read more'}`}
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default BuildingDescription;
